// react
import { useMemo } from 'react';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import { Grid, Skeleton } from '@mui/material';
import { Stack } from '@mui/system';

// api
import type { PortfolioAnalysisResult } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import { useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import { useGetSubscriptionSubscriptionAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisSubscription';
import { useGetV2AlarmsQuery } from '@/api/generated/recalc/endpoints/v2/alarms';
import { useGetEnergyGroupsQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';

// feature-components
import PortfolioModule from '@/features/energyxplorer/components/portfolio-dashboard/PortfolioModule';
import AlarmCard from '@/features/energyxplorer/components/sales-cards/AlarmCard';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { useHasService } from '@/hooks/useHasService';

// relative
import { filterPortfolioAlarms } from './portfolio-dashboard/alarmScope';

// rtk query
import { skipToken } from '@reduxjs/toolkit/query';

type PortfolioSubscription =
  { power?: boolean; water?: boolean; heat?: boolean } | undefined;

const PortfolioDashboardPage = () => {
  const { accountId } = useParams({ strict: false });
  const numericAccountId = accountId ? Number(accountId) : undefined;

  const energyGroupsQueryArg = numericAccountId
    ? { accountIds: [numericAccountId] }
    : skipToken;

  const { currentData: energyGroups } =
    useGetEnergyGroupsQuery(energyGroupsQueryArg);
  const hasLoadedEnergyGroups = energyGroups !== undefined;

  const { hasService: hasPortfolioAnalysisService } = useHasService(
    'PortfolioAnalysis',
  ) as unknown as { hasService: boolean };
  const { hasService: hasAnalyticsService } = useHasService(
    'EnergyXplorerAnalytics',
  ) as unknown as {
    hasService: boolean;
  };
  const { isAdmin } = useSessionAuth();

  const subscriptionQueryArg = numericAccountId
    ? { accountId: numericAccountId }
    : skipToken;

  const { currentData: subscriptionData, isLoading: isSubscriptionLoading } =
    useGetSubscriptionSubscriptionAccountIdGetQuery(subscriptionQueryArg, {
      skip: !numericAccountId || (!hasPortfolioAnalysisService && !isAdmin),
    });

  const typedSubscription = subscriptionData as PortfolioSubscription;

  const { currentData: powerData, isLoading: isPowerLoading } =
    useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
      numericAccountId
        ? { accountId: numericAccountId, analysisType: 'power' }
        : skipToken,
      { skip: !numericAccountId || !typedSubscription?.power },
    );

  const { currentData: waterData, isLoading: isWaterLoading } =
    useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
      numericAccountId
        ? { accountId: numericAccountId, analysisType: 'water' }
        : skipToken,
      { skip: !numericAccountId || !typedSubscription?.water },
    );

  const { currentData: heatData, isLoading: isHeatLoading } =
    useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
      numericAccountId
        ? { accountId: numericAccountId, analysisType: 'heat' }
        : skipToken,
      { skip: !numericAccountId || !typedSubscription?.heat },
    );

  const portfolioTotals = useMemo(() => {
    const aggregate = (list: PortfolioAnalysisResult[] | undefined) => {
      if (!list?.length) return { current: undefined, last: undefined };
      const filtered = list.filter((item) => item.type_of_mp !== 'PV');
      return {
        current: filtered.reduce(
          (sum, item) => sum + (item.total_consumption_year ?? 0),
          0,
        ),
        last: filtered.reduce(
          (sum, item) => sum + (item.total_consumption_last_year ?? 0),
          0,
        ),
      };
    };

    return {
      power: aggregate(powerData?.portfolio_analysis_list),
      heat: aggregate(heatData?.portfolio_analysis_list),
      water: aggregate(waterData?.portfolio_analysis_list),
    };
  }, [powerData, heatData, waterData]);

  const portfolioTreeIds = useMemo(
    () => ({
      power: energyGroups?.find((group) => group.type === 'PortfolioPower')
        ?.energyGroupId,
      heat: energyGroups?.find((group) => group.type === 'PortfolioHeat')
        ?.energyGroupId,
      water: energyGroups?.find((group) => group.type === 'PortfolioWater')
        ?.energyGroupId,
    }),
    [energyGroups],
  );

  const { currentData: portfolioAlarms, isLoading: isPortfolioAlarmsLoading } =
    useGetV2AlarmsQuery(
      numericAccountId && hasLoadedEnergyGroups
        ? {
            accountIds: [numericAccountId],
            orderBy: 'Created',
            orderDirection: 'desc',
          }
        : skipToken,
      {
        skip:
          !numericAccountId || !hasAnalyticsService || !hasLoadedEnergyGroups,
      },
    );

  const portfolioAlarmGroupIds = useMemo(
    () =>
      [
        portfolioTreeIds.power,
        portfolioTreeIds.heat,
        portfolioTreeIds.water,
      ].filter((groupId): groupId is number => typeof groupId === 'number'),
    [portfolioTreeIds],
  );

  const portfolioScopedAlarms = useMemo(
    () => filterPortfolioAlarms(portfolioAlarms ?? [], portfolioAlarmGroupIds),
    [portfolioAlarms, portfolioAlarmGroupIds],
  );

  const isLoading =
    isSubscriptionLoading || isPowerLoading || isWaterLoading || isHeatLoading;

  if (isLoading) {
    return (
      <PageContainer>
        <Grid container spacing={3}>
          {[1, 2].map((item) => (
            <Grid key={item} size={{ xs: 12, sm: 12, md: 12 }}>
              <Skeleton variant="rectangular" height={500} />
            </Grid>
          ))}
        </Grid>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ py: 2 }}>
      <Stack spacing={2}>
        <PortfolioModule
          accountId={accountId}
          portfolioTotals={portfolioTotals}
          typedSubscription={typedSubscription}
          portfolioAlarms={portfolioScopedAlarms}
          isPortfolioAlarmsLoading={
            isPortfolioAlarmsLoading ||
            (Boolean(numericAccountId) &&
              hasAnalyticsService &&
              !hasLoadedEnergyGroups)
          }
          powerData={powerData}
          heatData={heatData}
          waterData={waterData}
          hasAnalyticsService={hasAnalyticsService}
        />

        {!hasAnalyticsService && (
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid size="grow">
              <AlarmCard />
            </Grid>
          </Grid>
        )}
      </Stack>
    </PageContainer>
  );
};

export default PortfolioDashboardPage;
